<template>
  <div ref="boxComponent" class="container box_component">
    <!-- Título y botón de nueva competición -->
    <div class="header-title">
      <h2>{{ $t('organizers.title') }}</h2>
    </div>

    <!-- Barra de búsqueda y filtros -->
    <div class="filter-bar">
      <input
          v-model="searchQuery"
          @input="currentPage = 1"
          type="text"
          :placeholder="$t('competitions.view.search')"
          class="search-input"
      />
      <select
          v-model="selectedCountry"
          @change="onFilterChange"
          class="country-select"
      >
        <option value="">{{ $t('organizers.filters.all_countries') }}</option>
        <option
            v-for="country in availableCountries"
            :key="country.code"
            :value="country.code"
        >
          {{ country.flag ? country.flag + ' ' : '' }}{{ country.code }}
        </option>
      </select>
    </div>
    <!-- 🔹 VISTA DE ATLETAS -->
    <template v-if="this.organizers.length > 0">
      <!-- Grid -->
      <div :class="['organizer-grid', filteredAndPaginatedOrganizers.length <= 2 ? 'single' : '']">
        <OrganizerListCard
            v-for="organizer in filteredAndPaginatedOrganizers"
            :key="organizer.id"
            :organizer="organizer"
            @viewOrganizer="handleViewOrganizer"
        />
      </div>
      <div class="pagination-controls" v-if="totalPages > 1">
        <button class="pagination-btn" @click="currentPage--" :disabled="currentPage === 1">
          {{ $t('organizers.pagination.prev') }}
        </button>

        <span class="pagination-text">
          {{ $t('organizers.pagination.page') }} {{ currentPage }} {{ $t('organizers.pagination.of') }} {{ totalPages }}
        </span>

        <button class="pagination-btn" @click="currentPage++" :disabled="currentPage === totalPages">
          {{ $t('organizers.pagination.next') }}
        </button>
      </div>
    </template>

    <p v-else class="no-organizers">{{ $t("organizers.empty_organizers") }}</p>
  </div>
    <loading-modal ref="loadingModal"></loading-modal>
</template>

<script>
import LoadingModal from "@/services/LoadingModal.vue";
import {mapActions, mapState} from "vuex";
import router from "@/router/router";
import OrganizerListCard from "@/components/organizer/OrganizerListCard.vue";
import {resolveResponsiveGridItemsPerPage} from "@/helpers/responsiveGridPagination";

export default {
  name: "OrganizersList",
  components: {LoadingModal,OrganizerListCard },
  data() {
    return {
      organizers:[],
      searchQuery: "",
      selectedCountry: "",
      isLoading: false,
      currentPage: 1,
      itemsPerPage: 12,
      resizeObserver: null,
      resizeFrame: null,
      observedContainerWidth: 0,
    };
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
    },
    selectedCountry() {
      this.currentPage = 1;
    }
  },
  computed: {
    ...mapState('user', ['isAuthenticated','user','token']),
    ...mapState("competition",["appNavigation"]),
    filteredOrganizers() {
      const query = this.searchQuery.toLowerCase();

      return this.organizers.filter(organizer => {
        const matchesSearch = (
            organizer.name?.toLowerCase().includes(query) ||
            organizer.surname?.toLowerCase().includes(query)
        );
        const matchesCountry = !this.selectedCountry || this.getOrganizerCountryCode(organizer) === this.selectedCountry;
        return matchesSearch && matchesCountry;
      });
    },
    filteredAndPaginatedOrganizers() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredOrganizers.slice(start, start + this.itemsPerPage);
    },

    totalPages() {
      return Math.ceil(this.filteredOrganizers.length / this.itemsPerPage);
    },

    availableCountries() {
      const seen = new Set();
      const countries = [];

      this.organizers.forEach(organizer => {
        const code = this.getOrganizerCountryCode(organizer);
        if (!code || seen.has(code)) {
          return;
        }
        seen.add(code);
        countries.push({
          code,
          flag: this.codeToFlag(code),
        });
      });

      return countries.sort((a, b) => a.code.localeCompare(b.code));
    }
  },
  async mounted() {
    await this.fetchOrganizers();
    if (this.appNavigation.organizers?.currentPage) {
      this.currentPage = this.appNavigation.organizers?.currentPage;
      this.searchQuery = this.appNavigation.organizers?.searchQuery;
      this.selectedCountry = this.appNavigation.organizers?.selectedCountry || "";
      this.clearAppNavigationZone("organizers");
    }
    this.setupResponsivePagination();
  },
  beforeUnmount() {
    this.teardownResponsivePagination();
  },
  methods: {
    ...mapActions("competition",["updateAppNavigationZone","clearAppNavigationZone"]),
    setupResponsivePagination() {
      this.$nextTick(() => {
        this.scheduleItemsPerPageUpdate();
        window.addEventListener("resize", this.scheduleItemsPerPageUpdate);
        window.addEventListener("orientationchange", this.scheduleItemsPerPageUpdate);

        if (typeof ResizeObserver === "undefined") {
          return;
        }

        const target = this.$refs.boxComponent;
        if (!target) {
          return;
        }

        this.resizeObserver = new ResizeObserver((entries) => {
          const entry = entries?.[0];
          const nextWidth = Math.round(entry?.contentRect?.width || 0);

          if (!nextWidth || nextWidth === this.observedContainerWidth) {
            return;
          }

          this.observedContainerWidth = nextWidth;
          this.scheduleItemsPerPageUpdate();
        });
        this.resizeObserver.observe(target);
      });
    },
    teardownResponsivePagination() {
      window.removeEventListener("resize", this.scheduleItemsPerPageUpdate);
      window.removeEventListener("orientationchange", this.scheduleItemsPerPageUpdate);
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
        this.resizeFrame = null;
      }
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    scheduleItemsPerPageUpdate() {
      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = null;
        this.updateItemsPerPage();
      });
    },
    updateItemsPerPage() {
      const target = this.$refs.boxComponent;
      if (!target) {
        return;
      }

      const containerWidth = Math.round(target.clientWidth || 0);
      if (!containerWidth) {
        return;
      }

      this.observedContainerWidth = containerWidth;

      const nextItemsPerPage = resolveResponsiveGridItemsPerPage(containerWidth, window.innerWidth);
      if (nextItemsPerPage !== this.itemsPerPage) {
        this.itemsPerPage = nextItemsPerPage;
      }

      const totalPages = Math.max(1, Math.ceil(this.filteredOrganizers.length / this.itemsPerPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
    },
    onFilterChange() {
      this.currentPage = 1;
    },
    getOrganizerCountryCode(organizer) {
      if (!organizer) {
        return "";
      }
      const rawCode = organizer.stats?.country_code || organizer.country || "";
      return rawCode ? rawCode.toUpperCase() : "";
    },
    codeToFlag(code = "") {
      if (!code || code.length < 2) {
        return "";
      }
      const letters = code
          .toUpperCase()
          .replace(/[^A-Z]/g, "")
          .slice(0, 2);
      if (letters.length < 2) {
        return "";
      }
      return letters
          .split("")
          .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join("");
    },
    async fetchOrganizers() {
      this.showLoading();
      try {
        const response = await this.$axios.get("/organizers/list", {
          headers: {
            Authorization: `Bearer ${this.token}`,
          },
        });

        if (response.data?.organizers && typeof response.data.organizers === 'object') {
          // 🔁 Convertir objeto a array usando Object.values
          this.organizers = Object.values(response.data.organizers);
        } else {
          console.warn("⚠️ La respuesta no contiene atletas válidos.");
          this.organizers = [];
        }
      } catch (error) {
        console.error("❌ Error al obtener las competiciones:", error);
      } finally {
        this.hideLoading();
      }
    },
    showLoading() {
      this.isLoading = true;
      this.$nextTick(() => {
        this.$refs.loadingModal.showLoading();
      });
    },
    hideLoading() {
      this.isLoading = false;
      this.$nextTick(() => {
        this.$refs.loadingModal.hideLoading();
      });
    },
    async handleViewOrganizer(organizerId) {
      this.updateAppNavigationZone({
        zone: "organizers",
        data: {
          currentPage: this.currentPage,
          searchQuery: this.searchQuery,
          selectedCountry: this.selectedCountry,
        }
      });
      await router.push(`/profile/${organizerId}`);
    },

  },
};
</script>

<style scoped>
.box_component {
  width: 100%;
  max-width: 100%;
  margin: 15px auto;
}
.loading-text {
  text-align: center;
  font-size: 16px;
  color: #666;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
}
.search-input {
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  max-width: 250px;
}
.header-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}
.filter-bar {
  display: flex;
  gap: 10px;
  margin-bottom: 15px;
  flex-wrap: wrap;
}
.search-input{
  flex: 1;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
}
.country-select {
  min-width: 160px;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: #fff;
}
.organizer-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
}
.organizer-grid.single {
  justify-content: center;
}

.organizer-grid.single .organizer-card {
  max-width: 250px;
  width: 100%;
}
.organizer-grid .organizer-card {
  max-width: 200px;
  width: 100%;
}

.no-organizers {
  text-align: center;
  font-size: 14px;
  color: #777;
}

.organizers-header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.pagination-btn {
  background-color: #4c56af;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #3b4491;
}

.pagination-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.pagination-text {
  font-size: 14px;
  color: #444;
}

@media (max-width: 576px) {
  .organizer-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 2fr));
    gap: 2px;
  }
  .organizer-grid .organizer-card {
    max-width: 150px;
  }

  .organizer-grid.single .organizer-card {
    max-width: 100%;
  }
}

@media (max-width: 768px) {

}
</style>